import { useEffect, useRef, useCallback } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useAuthStore } from '@/stores/useAuthStore'
import { refreshAccessToken } from '@/api/axiosApi'

export interface SSEMessage {
  [key: string]: unknown
}

/** Typed payload for progress-tracking SSE streams (processing, export, upload). */
export interface ProgressSSEMessage extends SSEMessage {
  percent?: number
  stage?: string
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  progress?: number
  message?: string
  error?: string
}

export interface SSEEvent {
  type: string // 'progress', 'complete', etc.
  data: SSEMessage
}

export interface UseSSEOptions {
  url: string | null
  /** Logical stream identity used to dedupe live connections. Defaults to the resolved URL. */
  streamId?: string | null
  onMessage?: (event: SSEEvent) => void
  onError?: (error: Error) => void
  onComplete?: (event: SSEEvent) => void
  onReconnect?: () => void
  /** Fires on transitions only: true on first/recovered open, false after `unhealthyThreshold` consecutive failures */
  onHealthChange?: (healthy: boolean) => void
  enabled?: boolean
  /** When true, failed or closed streams reconnect using hook-owned backoff (default: false) */
  retry?: boolean
  /** Initial milliseconds between retries when retry is enabled (default: 3000). Grows exponentially up to maxRetryInterval. */
  retryInterval?: number
  /** Cap on the retry interval after exponential backoff (default: 60000) */
  maxRetryInterval?: number
  /** Backoff growth factor - each consecutive failure multiplies the delay (default: 2) */
  backoffMultiplier?: number
  /** Number of consecutive failures before onHealthChange(false) fires (default: 2) */
  unhealthyThreshold?: number
  /** When true, auto-disconnect on COMPLETED/FAILED status (default: true) */
  autoDisconnectOnTerminal?: boolean
  /** When true, keep the SSE connection open even when the tab is hidden (default: false) */
  openWhenHidden?: boolean
}

interface ActiveSSEStream {
  ownerId: number
  connectionId: number
  abortController: AbortController | null
  cancelReconnect: () => void
}

const activeStreams = new Map<string, ActiveSSEStream>()
let nextOwnerId = 1

/**
 * Generic SSE hook for subscribing to server-sent events
 * Uses @microsoft/fetch-event-source to support Bearer token authentication
 * Automatically disconnects on COMPLETED or FAILED status
 * Cleans up on unmount
 *
 * @param options.url - SSE endpoint URL (can be relative or absolute)
 * @param options.streamId - Logical stream identity for deduping connections
 * @param options.onMessage - Called for each message received
 * @param options.onError - Called on connection or parsing errors
 * @param options.onComplete - Called when status is COMPLETED or FAILED
 * @param options.enabled - Whether to connect (default: true when url is provided)
 */
export function useSSE({
  url,
  streamId,
  onMessage,
  onError,
  onComplete,
  onReconnect,
  onHealthChange,
  enabled = true,
  retry = false,
  retryInterval = 3000,
  maxRetryInterval = 60_000,
  backoffMultiplier = 2,
  unhealthyThreshold = 2,
  autoDisconnectOnTerminal = true,
  openWhenHidden = false,
}: UseSSEOptions) {
  const ownerIdRef = useRef<number | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const connectionIdRef = useRef(0)
  const activeStreamIdRef = useRef<string | null>(null)
  const accessToken = useAuthStore(state => state.accessToken)

  if (ownerIdRef.current === null) {
    ownerIdRef.current = nextOwnerId
    nextOwnerId += 1
  }

  // Store callbacks in refs to avoid re-running the effect when they change
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  const onCompleteRef = useRef(onComplete)
  const onReconnectRef = useRef(onReconnect)
  const onHealthChangeRef = useRef(onHealthChange)

  // Update refs on each render so we always call the latest callback
  onMessageRef.current = onMessage
  onErrorRef.current = onError
  onCompleteRef.current = onComplete
  onReconnectRef.current = onReconnect
  onHealthChangeRef.current = onHealthChange

  const cancelReconnect = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const disconnect = useCallback(() => {
    connectionIdRef.current += 1
    cancelReconnect()

    const activeStreamId = activeStreamIdRef.current
    if (activeStreamId) {
      const activeStream = activeStreams.get(activeStreamId)
      if (activeStream?.ownerId === ownerIdRef.current) {
        activeStreams.delete(activeStreamId)
      }
      activeStreamIdRef.current = null
    }

    const abortController = abortControllerRef.current
    abortControllerRef.current = null
    if (abortController && !abortController.signal.aborted) {
      abortController.abort()
    }
  }, [cancelReconnect])

  useEffect(() => {
    // Don't connect if disabled or no URL
    if (!enabled || !url) {
      disconnect()
      return
    }

    // Build the full URL
    let fullUrl: string
    try {
      // Check if it's already an absolute URL
      if (url.startsWith('http://') || url.startsWith('https://')) {
        fullUrl = url
      } else {
        // Relative URL - prepend API base URL
        const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
        fullUrl = `${baseUrl}${url}`
      }
    } catch (e) {
      onErrorRef.current?.(
        new Error(`Invalid SSE URL: ${url}`.concat(e as string))
      )
      return
    }

    const effectiveStreamId = streamId ?? fullUrl

    // Track whether this is the initial connection or a reconnection
    let hasConnected = false
    // Consecutive failures since the last successful open, drives backoff + health reporting
    let consecutiveFailures = 0
    // null = no transition reported yet; gates onHealthChange so we only fire on flips
    let isHealthy: boolean | null = null
    let stopped = false

    const isCurrentConnection = (connectionId: number) => {
      const activeStream = activeStreams.get(effectiveStreamId)
      return (
        !stopped &&
        connectionIdRef.current === connectionId &&
        activeStream?.ownerId === ownerIdRef.current &&
        activeStream.connectionId === connectionId
      )
    }

    const setActiveAbortController = (
      connectionId: number,
      abortController: AbortController | null
    ) => {
      const activeStream = activeStreams.get(effectiveStreamId)
      if (
        activeStream?.ownerId === ownerIdRef.current &&
        activeStream.connectionId === connectionId
      ) {
        activeStream.abortController = abortController
      }
    }

    const releaseConnection = (
      connectionId: number,
      abortController: AbortController
    ) => {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
      setActiveAbortController(connectionId, null)
    }

    const getRetryDelay = () => {
      const exp = Math.min(
        retryInterval *
          backoffMultiplier ** Math.max(consecutiveFailures - 1, 0),
        maxRetryInterval
      )
      // Jitter to 50-100% of the computed delay so clients desync.
      return exp * (0.5 + Math.random() * 0.5)
    }

    const scheduleReconnect = (connectionId: number, delay: number) => {
      if (!retry || !isCurrentConnection(connectionId)) return

      cancelReconnect()
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null
        openConnection()
      }, delay)
    }

    const normalizeError = (error: unknown) =>
      error instanceof Error ? error : new Error('SSE connection error')

    const unregisterCurrentStream = () => {
      const activeStream = activeStreams.get(effectiveStreamId)
      if (activeStream?.ownerId === ownerIdRef.current) {
        activeStreams.delete(effectiveStreamId)
      }
      if (activeStreamIdRef.current === effectiveStreamId) {
        activeStreamIdRef.current = null
      }
    }

    const handleConnectionFailure = (
      error: unknown,
      connectionId: number,
      abortController: AbortController
    ) => {
      releaseConnection(connectionId, abortController)

      if (!isCurrentConnection(connectionId)) return

      const normalizedError = normalizeError(error)
      onErrorRef.current?.(normalizedError)

      if (!retry) {
        unregisterCurrentStream()
        return
      }

      consecutiveFailures += 1
      if (consecutiveFailures >= unhealthyThreshold && isHealthy !== false) {
        isHealthy = false
        onHealthChangeRef.current?.(false)
      }

      scheduleReconnect(connectionId, getRetryDelay())
    }

    const pauseForHiddenTab = () => {
      cancelReconnect()

      const abortController = abortControllerRef.current
      abortControllerRef.current = null
      setActiveAbortController(connectionIdRef.current, null)

      if (abortController && !abortController.signal.aborted) {
        abortController.abort()
      }
    }

    function openConnection() {
      if (stopped) return

      cancelReconnect()

      if (!openWhenHidden && document.hidden) {
        pauseForHiddenTab()
        return
      }

      const existingStream = activeStreams.get(effectiveStreamId)
      if (existingStream) {
        existingStream.cancelReconnect()
        if (
          existingStream.abortController &&
          !existingStream.abortController.signal.aborted
        ) {
          existingStream.abortController.abort()
        }
        activeStreams.delete(effectiveStreamId)
      }

      connectionIdRef.current += 1
      const connectionId = connectionIdRef.current
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      activeStreamIdRef.current = effectiveStreamId
      activeStreams.set(effectiveStreamId, {
        ownerId: ownerIdRef.current!,
        connectionId,
        abortController,
        cancelReconnect,
      })

      // Pre-flight: if the access token is already expired, refresh and bail.
      // setTokens updates the store synchronously, which flips the accessToken
      // dep and re-runs this effect with the fresh closure. Falling through
      // here would open fetchEventSource with the stale closure token.
      if (useAuthStore.getState().isExpired()) {
        refreshAccessToken()
          .catch(() => {
            // store cleared by doRefresh; effect re-runs with accessToken=null
          })
          .finally(() => {
            releaseConnection(connectionId, abortController)
          })
        return
      }

      let closedCleanly = false

      // Start SSE connection with Bearer token authentication
      fetchEventSource(fullUrl, {
        method: 'GET',
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        signal: abortController.signal,
        onopen: async response => {
          if (response.status === 401) {
            // Stale token. Trigger a shared refresh; the store update will
            // re-run this effect (accessToken is in the dep array) and reopen
            // with fresh credentials. Throw so our catch path can schedule a
            // backoff as a safety net in case refresh is slow.
            try {
              await refreshAccessToken()
            } catch {
              // refreshAccessToken already cleared the auth store, which
              // sets accessToken=null, then this effect re-runs and returns.
            }
            throw new Error('SSE 401: refreshing token')
          }
          if (!response.ok) {
            throw new Error(`SSE connection failed: ${response.status}`)
          }
          cancelReconnect()
          consecutiveFailures = 0
          if (isHealthy !== true) {
            isHealthy = true
            onHealthChangeRef.current?.(true)
          }
          if (hasConnected) {
            onReconnectRef.current?.()
          }
          hasConnected = true
        },
        onmessage: event => {
          try {
            const rawData = event.data?.trim() ?? ''

            // Ignore heartbeat/keepalive comment frames that carry no event name/data.
            if (!event.event && rawData.length === 0) {
              return
            }

            const data: SSEMessage =
              rawData.length > 0 ? JSON.parse(rawData) : {}
            const sseEvent: SSEEvent = { type: event.event || 'message', data }

            onMessageRef.current?.(sseEvent)

            // Terminal state detection: 'complete'/'error' event OR COMPLETED/FAILED status
            if (
              autoDisconnectOnTerminal &&
              (event.event === 'complete' ||
                event.event === 'error' ||
                data.status === 'COMPLETED' ||
                data.status === 'FAILED')
            ) {
              onCompleteRef.current?.(sseEvent)
              disconnect()
            }
          } catch (e) {
            onErrorRef.current?.(new Error(`Failed to parse SSE message: ${e}`))
          }
        },
        onerror: error => {
          throw error
        },
        // Visibility reconnects are managed by this hook so they share the
        // same single-controller/single-timer lifecycle as normal retries.
        openWhenHidden: true,
        onclose: () => {
          closedCleanly = true
        },
      })
        .then(() => {
          releaseConnection(connectionId, abortController)

          if (!isCurrentConnection(connectionId)) return

          if (closedCleanly && retry) {
            scheduleReconnect(connectionId, getRetryDelay())
            return
          }

          unregisterCurrentStream()
        })
        .catch(error => {
          // AbortError is expected when we disconnect intentionally.
          if (
            error instanceof Error &&
            (error.name === 'AbortError' || abortController.signal.aborted)
          ) {
            releaseConnection(connectionId, abortController)
            return
          }

          handleConnectionFailure(error, connectionId, abortController)
        })
    }

    const handleVisibilityChange = () => {
      if (openWhenHidden) return
      if (document.hidden) {
        pauseForHiddenTab()
        return
      }
      cancelReconnect()
      openConnection()
    }

    if (!openWhenHidden) {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    if (openWhenHidden || !document.hidden) {
      openConnection()
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      stopped = true
      if (!openWhenHidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
      disconnect()
    }
  }, [
    url,
    streamId,
    enabled,
    accessToken,
    disconnect,
    cancelReconnect,
    retry,
    retryInterval,
    maxRetryInterval,
    backoffMultiplier,
    unhealthyThreshold,
    autoDisconnectOnTerminal,
    openWhenHidden,
  ])

  return { disconnect }
}
